<template>
  <div class="checkout-page">
    <first-run-intent-strip />
    <div class="checkout-page__inner">
      <div class="checkout-page__logo"><auth-logo /></div>
      <div class="checkout-page__head">
        <h1 class="pop-auth__h1">Confirm your plan</h1>
        <p class="pop-auth__sub" style="margin: 0">You are signing up as <b>{{ user.email }}</b></p>
      </div>

      <div v-if="!plan" class="checkout-page__card checkout-page__card--center">
        <p class="pop-auth__sub">Pick a plan first.</p>
        <router-link class="pop-auth__btn pop-auth__btn--primary checkout-page__inline-btn" to="/plans">Go to plans</router-link>
      </div>

      <div v-else-if="result === 'invoice'" class="checkout-page__card checkout-page__card--center" data-test="invoice-sent">
        <h3 class="checkout-page__card-title">Invoice sent</h3>
        <p class="pop-auth__sub">Invoice sent to <b>{{ user.email }}</b>. Your plan is active now; the invoice is due within 7 days.</p>
        <a v-if="hostedInvoiceUrl" class="pop-auth__link" :href="hostedInvoiceUrl" target="_blank" rel="noopener">Open the invoice</a>
        <button type="button" class="pop-auth__btn pop-auth__btn--primary checkout-page__inline-btn" @click="goToDashboard">Open my account</button>
      </div>

      <div v-else class="checkout-page__grid">
        <div class="checkout-page__card">
          <div class="checkout-page__card-head">
            <h3 class="checkout-page__card-title">Order summary</h3>
            <button v-if="!couponOpen" type="button" class="pop-auth__link checkout-page__small" @click="couponOpen = true">+ Add coupon code</button>
          </div>
          <div class="checkout-page__rows">
            <div class="checkout-page__row"><span>POP {{ plan.title }} Plan</span><span>{{ formatPrice(plan.price) }} / {{ plan.interval }}</span></div>

            <div v-if="couponOpen">
              <label class="pop-auth__label" for="checkout-coupon">Coupon code</label>
              <div class="checkout-page__coupon">
                <input id="checkout-coupon" v-model="couponCode" class="pop-auth__field" :class="{ 'is-valid': preview && preview.valid }" placeholder="Enter code" data-test="checkout-coupon" @input="preview = null; couponError = ''" @keyup.enter="applyCoupon" />
                <button type="button" class="pop-auth__btn pop-auth__btn--secondary checkout-page__apply" :disabled="couponBusy || couponCode.trim().length < 3" @click="applyCoupon">{{ couponBusy ? 'Checking' : 'Apply' }}</button>
              </div>
              <div v-if="couponError" class="pop-auth__err">{{ couponError }}</div>
              <div v-else-if="preview && preview.valid" class="pop-auth__hint checkout-page__applied">{{ appliedText }}</div>
            </div>

            <div v-if="affiliateApplied && !couponOpen" class="pop-auth__hint checkout-page__applied" data-test="checkout-affiliate">
              Partner link applied: your signup is credited to your referrer. Add a coupon code above if you have one.
            </div>

            <div v-if="discount > 0" class="checkout-page__row checkout-page__row--green"><span>Coupon {{ preview.code }}</span><span>-{{ formatPrice(discount) }}</span></div>
            <div v-if="credits" class="checkout-page__row checkout-page__row--green"><span>Coupon {{ preview.code }}</span><span>+{{ credits }} credits added to your account</span></div>

            <div class="checkout-page__row checkout-page__row--total"><span>Due today</span><span>{{ formatPrice(due) }} USD</span></div>

            <label v-if="invoiceAvailable" class="checkout-page__invoice">
              <input v-model="payViaInvoice" type="checkbox" />
              <span>I want to pay via invoice</span>
            </label>

            <div v-if="submitError" class="pop-auth__box pop-auth__box--danger" data-test="checkout-error">{{ submitError }}</div>

            <button type="button" class="pop-auth__btn pop-auth__btn--primary checkout-page__cta" :disabled="busy" data-test="checkout-continue" @click="submit">
              {{ ctaLabel }}
            </button>

            <div v-if="isPeriodFree" class="pop-auth__fine">No card needed. Your {{ preview.freeDays }}-day free period starts now.</div>
            <div v-else-if="payViaInvoice" class="pop-auth__fine">We will email a Stripe invoice to {{ user.email }}.</div>
            <div v-else class="pop-auth__fine checkout-page__lock">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7B7E" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              Card, Apple Pay and Link, processed by Stripe. POP never stores your card.
            </div>
            <p v-if="!isPeriodFree" class="pop-auth__fine" style="margin: 0">
              {{ renewalText }}
              Cancel within 7 days for a full refund. Subscriptions are cancelled in Account settings, not by email or chat.
            </p>
          </div>
        </div>

        <div class="checkout-page__side">
          <div class="checkout-page__card">
            <div class="checkout-page__card-title">{{ plan.title }} includes</div>
            <div v-if="catalogPlan.creditLine" class="pop-auth__fine" style="margin-bottom: 12px">{{ catalogPlan.creditLine }}</div>
            <ul class="checkout-page__bullets">
              <li v-for="bullet in catalogPlan.bullets" :key="bullet">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#25A97A" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                <span>{{ bullet }}</span>
              </li>
            </ul>
            <router-link class="pop-auth__link checkout-page__small" to="/plans" style="display: inline-block; margin-top: 14px">Change plan</router-link>
          </div>
          <div v-if="!hasActivePlan" class="checkout-page__card checkout-page__card--dashed">
            Want to see it first?
            <button type="button" class="pop-auth__link" data-test="skip-payment" @click="skipPayment">Skip payment and open the demo account</button>.
            Your plan choice is saved.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthLogo from '../AuthLogo.vue'
import FirstRunIntentStrip from '../FirstRunIntentStrip.vue'
import { afterPlanRoute } from '../first-run'
import { formatPrice, planById, round2 } from '../plans'
import { segmentTrack } from '@/utils'

const ACTIVE_STATES = ['active', 'free_period', 'admin', 'exempt', 'lapsed']

export default {
  name: 'PlanCheckoutPage',
  components: { AuthLogo, FirstRunIntentStrip },
  data() {
    return {
      couponOpen: false,
      couponCode: '',
      affiliateApplied: false, // affiliate link without a discount: referral noted, no code in the coupon field
      couponBusy: false,
      couponError: '',
      preview: null, // plans_coupon_preview payload
      payViaInvoice: false,
      invoiceEnabled: false,
      invoiceEligibleCodes: [],
      busy: false,
      submitError: '',
      result: '',
      hostedInvoiceUrl: '',
    }
  },
  computed: {
    user() {
      return this.$store.state.user || {}
    },
    signup() {
      return this.$store.state.signup
    },
    plan() {
      return this.signup.plan
    },
    catalogPlan() {
      return planById(this.plan && this.plan.id) || { bullets: [], creditLine: '' }
    },
    hasActivePlan() {
      if (this.user.planState) return ACTIVE_STATES.includes(this.user.planState)
      return Boolean(this.user.activeSubscriber)
    },
    isPeriodFree() {
      return Boolean(this.preview && this.preview.valid && this.preview.type === 'PERIOD_FREE')
    },
    discount() {
      if (!this.preview || !this.preview.valid) return 0
      if (this.preview.type === 'PERCENT_OFF' || this.preview.type === 'AMOUNT_OFF') return Number(this.preview.discount || 0)
      return 0
    },
    credits() {
      return this.preview && this.preview.valid && this.preview.type === 'CREDITS' ? this.preview.credits : 0
    },
    due() {
      if (!this.plan) return 0
      if (this.isPeriodFree) return 0
      return round2(Math.max(0, this.plan.price - this.discount))
    },
    appliedText() {
      const p = this.preview
      if (!p) return ''
      const prefix = this.signup.entry === 'affiliate' ? 'Partner code applied from your link: ' : 'Applied: '
      if (p.type === 'PERCENT_OFF') return `${prefix}${p.amount}% off ${this.discountPeriod}.`
      if (p.type === 'AMOUNT_OFF') return `${prefix}${formatPrice(p.amount)} off ${this.discountPeriod}.`
      if (p.type === 'CREDITS') return `${prefix}+${p.credits} credits added to your account.`
      if (p.type === 'PERIOD_FREE') return `${prefix}${p.freeDays} days free.`
      if (p.type === 'AFFILIATE') return 'Partner code recorded. It does not carry a discount.'
      return ''
    },
    // How long a discount lasts, from the coupon's Stripe duration: once, repeating N months, or forever.
    discountPeriod() {
      const p = this.preview || {}
      const interval = this.plan ? this.plan.interval : 'month'
      if (p.duration === 'once') return `your first ${interval}`
      if (p.duration === 'repeating' && p.durationInMonths) return `your first ${p.durationInMonths} months`
      return `every ${interval}`
    },
    renewalText() {
      const p = this.preview || {}
      const interval = this.plan.interval
      const full = formatPrice(this.plan.price)
      if (this.discount > 0 && p.duration === 'forever') {
        return `Your card is charged ${formatPrice(this.due)} now and renews at ${formatPrice(this.due)} every ${interval} while the code stays valid.`
      }
      if (this.discount > 0 && p.duration === 'repeating' && p.durationInMonths) {
        return `Your card is charged ${formatPrice(this.due)} now, keeps the discount for ${p.durationInMonths} months, then renews at ${full} every ${interval}.`
      }
      return `Your card is charged ${formatPrice(this.due)} now and renews at ${full} every ${interval}.`
    },
    invoiceAvailable() {
      return this.invoiceEnabled && this.plan && this.plan.interval === 'year' && !this.isPeriodFree
        && (!this.invoiceEligibleCodes.length || this.invoiceEligibleCodes.includes(this.plan.code))
    },
    ctaLabel() {
      if (this.busy) return 'One moment'
      if (this.isPeriodFree) return 'Start your free period'
      if (this.payViaInvoice) return 'Send me an invoice'
      return 'Continue to secure checkout'
    },
  },
  async mounted() {
    if (!this.plan) return
    if (this.$route.query.canceled === '1') {
      this.$notify({ group: 'info', type: 'warning', text: 'Checkout cancelled. Your plan choice is saved.' })
    }
    const code = this.signup.promoCode || this.signup.affiliateCode || ''
    const fromAffiliateLink = Boolean(code) && code.toUpperCase() === (this.signup.affiliateCode || '').toUpperCase()
    if (fromAffiliateLink) {
      this.applyAffiliateLink(code)
    } else if (code) {
      this.couponCode = code
      this.couponOpen = true
      this.applyCoupon()
    }
    try {
      const response = await this.$store.dispatch('fetchPlanCatalog')
      if (response && response.data) {
        this.invoiceEnabled = Boolean(response.data.invoiceEnabled)
        this.invoiceEligibleCodes = response.data.invoiceEligibleCodes || []
      }
    } catch (e) {
      console.log(e)
    }
  },
  methods: {
    formatPrice,
    // The partner code of an affiliate link is not a coupon by itself: only when the affiliate has a
    // discount attached does it belong in the coupon field. A bare referral is noted without a code,
    // so the field stays free for a real coupon (tester, 2026-10-21).
    async applyAffiliateLink(code) {
      if (!this.plan) return
      try {
        const response = await this.$store.dispatch('previewCoupon', { code: code.trim().toUpperCase(), servicePackage: this.plan.code })
        const data = response.data || {}
        if (data.status === 'SUCCESS' && data.valid && data.type !== 'AFFILIATE') {
          this.couponCode = data.code || code.trim().toUpperCase()
          this.couponOpen = true
          this.preview = data
          this.$store.commit('signup/patch', { promoCode: this.couponCode })
          return
        }
        this.affiliateApplied = data.status === 'SUCCESS' && Boolean(data.valid)
      } catch (e) {
        console.log(e)
      }
    },
    async applyCoupon() {
      const code = this.couponCode.trim().toUpperCase()
      if (!code || !this.plan) return
      this.couponBusy = true
      this.couponError = ''
      try {
        const response = await this.$store.dispatch('previewCoupon', { code, servicePackage: this.plan.code })
        const data = response.data || {}
        if (data.status === 'SUCCESS' && data.valid) {
          this.preview = data
          this.couponCode = data.code || code
          this.$store.commit('signup/patch', { promoCode: this.couponCode })
        } else {
          this.preview = null
          this.couponError = data.msg || 'This promo code is not valid.'
        }
      } catch (e) {
        console.log(e)
        this.couponError = 'Could not check this code right now.'
      } finally {
        this.couponBusy = false
      }
    },
    beginCheckoutForGtm() {
      try {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({ ecommerce: null })
        window.dataLayer.push({ event: 'begin_checkout', ecommerce: { value: this.due, currency: 'USD', items: [{
          item_id: this.plan.code, item_name: `POP ${this.plan.title}`,
          item_category: this.plan.interval === 'year' ? 'Yearly' : 'Monthly', price: this.due, discount: this.discount }] } })
      } catch (e) {
        console.log(e)
      }
    },
    async submit() {
      if (!this.plan || this.busy) return
      this.busy = true
      this.submitError = ''
      const method = this.isPeriodFree ? 'free' : this.payViaInvoice ? 'invoice' : 'stripe'
      segmentTrack('checkout_started', { servicePackage: this.plan.code, promoCode: this.couponCode, due: this.due, method })
      this.beginCheckoutForGtm()
      try {
        const response = await this.$store.dispatch('createCheckoutSession', {
          servicePackage: this.plan.code,
          couponCode: this.preview && this.preview.valid ? this.couponCode.trim().toUpperCase() : '',
          payViaInvoice: this.payViaInvoice,
        })
        this.handle(response.data || {})
      } catch (e) {
        console.log(e)
        this.submitError = 'Could not start the checkout. Please try again.'
        this.busy = false
      }
    },
    handle(data) {
      switch (data.action) {
        case 'CHECKOUT':
          this.$store.commit('signup/patch', { checkoutSessionId: data.sessionId })
          setTimeout(() => window.location.assign(data.checkoutUrl), 50) // let GTM flush
          break
        case 'PLAN_ACTIVATED':
          this.$store.commit('setUser', data.user)
          segmentTrack('purchase', { servicePackage: this.plan.code, promoCode: this.couponCode, method: 'free' })
          this.$router.replace(this.afterPlan())
          break
        case 'INVOICE_SENT':
          if (data.user) this.$store.commit('setUser', data.user)
          this.hostedInvoiceUrl = data.hostedInvoiceUrl || ''
          this.result = 'invoice'
          this.busy = false
          break
        case 'ALREADY_SUBSCRIBED':
          this.submitError = data.msg
          this.busy = false
          break
        default:
          this.submitError = data.msg || 'Could not start the checkout. Please try again.'
          if (String(data.action || '').startsWith('COUPON_')) {
            this.preview = null
            this.couponError = data.msg
          }
          this.busy = false
      }
    },
    skipPayment() {
      segmentTrack('plan_skipped', { fromCheckout: true, servicePackage: this.plan && this.plan.code })
      this.$router.push('/sites')
    },
    goToDashboard() {
      this.$router.replace(this.afterPlan())
    },
    // the prefilled first run when one was saved from the example report, else the dashboard
    afterPlan() {
      const target = afterPlanRoute(this.$store)
      this.$store.commit('signup/reset')
      return target
    },
  },
}
</script>

<style scoped lang="scss">
@import '../auth-tokens.scss';

.checkout-page {
  min-height: 100vh;
  background: $pop-page-bg;
  padding: 32px 5vw 110px;
  color: $pop-teal;
  font-family: 'Mulish', sans-serif;
  text-align: left;

  &__inner { max-width: 880px; margin: 0 auto; }
  &__logo { display: flex; }
  &__head { text-align: center; margin: 44px 0 30px; }
  &__grid { display: grid; grid-template-columns: minmax(0, 3fr) minmax(0, 2fr); gap: 20px; }
  &__side { display: grid; gap: 16px; align-content: start; }
  &__card {
    background: #fff; border: 1px solid #e3eaeb; border-radius: 18px; padding: 22px 24px;
    box-shadow: 0 10px 30px -18px rgba(1, 71, 81, 0.18);
    &--dashed { background: #f7f7f7; border: 1px dashed #cccccc; box-shadow: none; font-size: 13px; line-height: 1.5; }
    &--center { text-align: center; }
  }
  &__card-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 14px; }
  &__card-title { font-family: 'Mulish', sans-serif; font-weight: 800; font-size: 16px; margin: 0 0 4px; }
  &__small { font-size: 12.5px; }
  &__rows { display: grid; gap: 14px; font-size: 14px; }
  &__row { display: flex; justify-content: space-between; gap: 12px; }
  &__row--green { color: $pop-success; }
  &__row--total { padding-top: 14px; border-top: 1px solid $pop-hairline; font-weight: 800; }
  &__coupon { display: flex; gap: 8px; }
  &__coupon .pop-auth__field.is-valid { border-color: $pop-success; }
  &__apply { width: auto; padding: 0 16px; font-size: 13px; }
  &__applied { color: $pop-success; font-weight: 600; }
  &__invoice { display: flex; align-items: center; gap: 10px; font-size: 13px; cursor: pointer; input { width: 16px; height: 16px; accent-color: $pop-link; } }
  &__cta { margin-top: 6px; font-size: 14.5px; padding: 13px 16px; }
  &__inline-btn { display: inline-flex; width: auto; padding: 13px 22px; margin-top: 16px; text-decoration: none; }
  &__lock { display: flex; gap: 8px; align-items: center; }
  &__bullets { list-style: none; padding: 0; margin: 0; display: grid; gap: 9px; li { display: flex; gap: 9px; font-size: 13px; line-height: 1.4; } svg { flex: none; margin-top: 2px; } }

  @media (max-width: 860px) {
    &__grid { grid-template-columns: minmax(0, 1fr); }
  }
}
</style>
